import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { FieldChange, TransactionEvent } from '@debtbot/shared';
import { CATEGORIES, type TransactionCategory } from '@debtbot/shared';
import {
  useConnection,
  useDeleteTransaction,
  useDisputeTransaction,
  useMe,
  useTransaction,
  useWithdrawDispute,
} from '../api/hooks';
import { Amount } from '../components/Amount';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { Sheet } from '../components/Sheet';
import { ListSkeleton } from '../components/Skeleton';
import { getLocale, useT } from '../i18n';
import { formatDateTime, formatMoney, fullName, txDelta, txEmoji, txTitle } from '../lib/format';
import { haptic } from '../telegram/haptic';
import { confirmDestructive } from '../telegram/ui';

function fieldValue(field: string, v: unknown, names: Record<string, string>): string {
  if (v === null || v === undefined) return '—';
  if (field === 'amount' && typeof v === 'number')
    return formatMoney({ amount: v, currency: 'UZS' });
  if (field === 'payer_id' && typeof v === 'string') return names[v] ?? '…';
  if (field === 'category' && typeof v === 'string')
    return CATEGORIES[v as TransactionCategory]?.label[getLocale()] ?? v;
  return String(v);
}

function EventLine({ e, names }: { e: TransactionEvent; names: Record<string, string> }) {
  const t = useT();
  const actor = fullName(e.actor);
  const label = t.tx.events[e.type] ?? e.type;
  let detail: string | null = null;
  if (e.type === 'UPDATED' && e.changes) {
    detail = Object.entries(e.changes as Record<string, FieldChange>)
      .filter(([k]) => k !== 'dispute_cleared')
      .map(
        ([k, ch]) =>
          `${t.tx.fields[k] ?? k}: ${fieldValue(k, ch.from, names)} → ${fieldValue(k, ch.to, names)}`,
      )
      .join('; ');
    if ('dispute_cleared' in e.changes)
      detail = [detail, t.tx.fields.dispute_cleared].filter(Boolean).join('; ');
  } else if (e.type === 'DISPUTED') {
    const reason = (e.changes as { reason?: string | null } | null)?.reason;
    if (reason) detail = `«${reason}»`;
  } else if (e.type === 'CREATED' && e.changes && 'amount' in e.changes) {
    detail = formatMoney({
      amount: Number((e.changes as { amount: number }).amount),
      currency: 'UZS',
    });
  }
  return (
    <li className="py-2 text-sm">
      <div>
        <b>{label}</b> · {actor} · <span className="text-hint">{formatDateTime(e.created_at)}</span>
      </div>
      {detail && <div className="text-hint">{detail}</div>}
    </li>
  );
}

export function TransactionScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const tx = useTransaction(id);
  const conn = useConnection(tx.data?.connection_id ?? '');
  const remove = useDeleteTransaction(tx.data?.connection_id ?? '');
  const dispute = useDisputeTransaction(id, tx.data?.connection_id ?? '');
  const withdraw = useWithdrawDispute(id, tx.data?.connection_id ?? '');
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (tx.isPending || me.isPending) {
    return (
      <main className="min-h-dvh bg-bg text-text px-4 py-4">
        <ListSkeleton rows={3} />
      </main>
    );
  }
  if (tx.isError || !tx.data || !me.data) {
    return (
      <main className="min-h-dvh bg-bg text-text px-4 py-4">
        <ErrorBanner error={tx.error} onRetry={() => void tx.refetch()} />
      </main>
    );
  }

  const data = tx.data;
  const meId = me.data.id;
  const peerName = conn.data ? fullName(conn.data.peer) : '…';
  const names: Record<string, string> = {
    [meId]: fullName(me.data),
    ...(conn.data && { [conn.data.peer.id]: peerName }),
  };
  const nameOf = (uid: string) => names[uid] ?? '…';
  const isCreator = data.created_by_id === meId;
  const deleted = data.deleted_at !== null;
  const disputedByMe = data.disputed_by_id === meId;

  const onDelete = async () => {
    if (!(await confirmDestructive(t.tx.confirmDelete, t.tx.delete))) return;
    try {
      await remove.mutateAsync(id);
      haptic.success();
      void navigate(-1);
    } catch {
      haptic.error();
    }
  };

  const onDispute = async () => {
    try {
      await dispute.mutateAsync({ reason: reason.trim() || null });
      haptic.success();
      setDisputeOpen(false);
    } catch {
      haptic.error();
    }
  };

  const direction =
    data.type === 'SETTLEMENT'
      ? t.tx.returned(
          nameOf(data.payer_id),
          data.beneficiary_id === meId ? t.settle.toMe : nameOf(data.beneficiary_id),
        )
      : t.tx.paidFor(nameOf(data.payer_id), nameOf(data.beneficiary_id));

  return (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 flex flex-col gap-4">
      {deleted && (
        <div className="rounded-xl bg-secondary-bg px-4 py-3 text-sm text-hint">
          🗑 {t.tx.deleted}
        </div>
      )}
      <header className="flex flex-col gap-1">
        <div className="text-3xl">{txEmoji(data)}</div>
        <h1 className="text-xl font-semibold">{txTitle(data)}</h1>
        <Amount
          money={txDelta(data, meId)}
          className={`text-3xl font-semibold ${data.status === 'DISPUTED' ? 'line-through opacity-70' : ''}`}
        />
        <div className="text-hint">{direction}</div>
        <div className="text-sm text-hint">
          {formatDateTime(data.occurred_at)} · {t.tx.addedBy(nameOf(data.created_by_id))}
        </div>
        {data.status === 'DISPUTED' && (
          <div className="rounded-xl bg-secondary-bg px-3 py-2 text-sm text-warning">
            ⚠ {t.tx.disputed}
            {data.dispute_reason && (
              <span className="text-text">
                {' '}
                · {t.tx.disputeReasonBy(nameOf(data.disputed_by_id!))}«{data.dispute_reason}»
              </span>
            )}
          </div>
        )}
      </header>

      <section>
        <h2 className="text-sm text-hint">{t.tx.history}</h2>
        <ul className="divide-y divide-secondary-bg">
          {data.events.map((e) => (
            <EventLine key={e.id} e={e} names={names} />
          ))}
        </ul>
      </section>

      {!deleted && (
        <section className="flex flex-col gap-2">
          {isCreator && (
            <>
              <Button data-testid="tx-edit" onClick={() => void navigate(`/tx/${id}/edit`)}>
                {t.tx.edit}
              </Button>
              <Button
                variant="secondary"
                data-testid="tx-delete"
                onClick={() => void onDelete()}
                disabled={remove.isPending}
              >
                {t.tx.delete}
              </Button>
            </>
          )}
          {!isCreator && data.status === 'ACTIVE' && (
            <Button
              variant="secondary"
              data-testid="tx-dispute"
              onClick={() => setDisputeOpen(true)}
            >
              {t.tx.dispute}
            </Button>
          )}
          {!isCreator && disputedByMe && (
            <Button
              variant="secondary"
              data-testid="tx-withdraw"
              onClick={() => withdraw.mutate()}
              disabled={withdraw.isPending}
            >
              {t.tx.withdraw}
            </Button>
          )}
        </section>
      )}

      <Sheet open={disputeOpen} onClose={() => setDisputeOpen(false)} title={t.tx.disputeTitle}>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-hint">{t.tx.disputeHint}</p>
          <input
            data-testid="dispute-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 200))}
            placeholder={t.tx.disputeReason}
            className="rounded-xl bg-secondary-bg px-4 py-3 outline-none placeholder:text-hint"
          />
          {dispute.isError && <ErrorBanner error={dispute.error} />}
          <Button
            data-testid="tx-dispute-confirm"
            onClick={() => void onDispute()}
            disabled={dispute.isPending}
          >
            {t.tx.dispute}
          </Button>
        </div>
      </Sheet>
    </main>
  );
}
